import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth } from '../utils/auth';
import type { ApiResponse } from '../types/api';
import AppShell from './AppShell';
import { Input } from './Input';
import './ChangePassword.css';

const ChangePassword: React.FC = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword.length < 8) {
      setError('新密码长度至少为 8 位');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setLoading(true);

    try {
      const requestMeta = {
        method: 'POST',
        url: `${API_BASE_URL}/user/change-password`,
        body: { oldPassword: oldPassword ? '••••' : '', newPassword: newPassword ? '••••' : '' },
      };

      const response = await fetch(requestMeta.url, {
        method: requestMeta.method,
        headers: getAuthHeaders(),
        body: JSON.stringify({ oldPassword, newPassword }),
      });

      let parsedBody: any = null;
      try {
        parsedBody = await response.json();
      } catch {
        parsedBody = null;
      }

      if (response.status === 401) {
        const serverMsg: string = parsedBody?.error || '';
        if (serverMsg && /password|当前|旧|incorrect|invalid/i.test(serverMsg)) {
          setError(serverMsg || '当前密码错误');
          return;
        }
        // Unmatched 401 — show debug info instead of immediate logout
        setDebugInfo({ request: requestMeta, response: { status: response.status, body: parsedBody } });
        setShowDebug(true);
        setError(serverMsg || '未识别的 401 错误，查看调试信息');
        return;
      }

      const data: ApiResponse = parsedBody;
      if (data?.success) {
        setSuccessMessage('密码修改成功');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const serverMsg: string = data?.error || '';
        if (serverMsg && /password|当前|旧|incorrect|invalid/i.test(serverMsg)) {
          setError(serverMsg);
          return;
        }
        // Unmatched server error — show debug info
        setDebugInfo({ request: requestMeta, response: { status: response.status, body: data } });
        setShowDebug(true);
        setError(serverMsg || '服务器返回错误，查看调试信息');
      }
    } catch (err: any) {
      setDebugInfo({ request: { url: `${API_BASE_URL}/user/change-password` }, response: { error: String(err) } });
      setShowDebug(true);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <div className="change-password-header app-shell-header-content">
      <button
        onClick={() => navigate('/profile/settings')}
        className="back-button"
        title="取消并返回"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>修改密码</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="change-password-container">
        <div className="password-content">
        <form onSubmit={handleSubmit} className="password-form" noValidate>
          <div className="form-group">
            <label htmlFor="oldPassword">当前密码</label>
            <div className="password-input-container">
              <Input
                type={showOldPassword ? "text" : "password"}
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                placeholder="请输入当前密码"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowOldPassword(!showOldPassword)}
                tabIndex={-1}
              >
                {showOldPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">新密码</label>
            <div className="password-input-container">
              <Input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                placeholder="请输入新密码（至少8位）"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
              >
                {showNewPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <small>密码长度至少为 8 位</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">确认新密码</label>
            <div className="password-input-container">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="请再次输入新密码"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {successMessage && <div className="success-message">{successMessage}</div>}
          {error && <div className="error-message">{error}</div>}

          {showDebug && debugInfo && (
            <div className="debug-panel" role="region" aria-live="polite">
              <div className="debug-panel__controls">
                <button
                  type="button"
                  className="debug-close"
                  onClick={() => setShowDebug(false)}
                >
                  隐藏调试信息
                </button>
                <button
                  type="button"
                  className="debug-copy"
                  onClick={() => {
                    try {
                      navigator.clipboard?.writeText(JSON.stringify(debugInfo, null, 2));
                      // quick feedback
                      alert('已复制调试信息到剪贴板');
                    } catch {
                      // ignore
                    }
                  }}
                >
                  复制
                </button>
              </div>
              <pre className="debug-panel__pre">{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="save-button"
            >
              {loading ? '修改中...' : '修改密码'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </AppShell>
  );
};

export default ChangePassword;
